import * as Ably from "ably"

type TokenEndpoint = "/ably/order_token" | "/ably/company_token"

type TokenResponse = {
  token: string
  expires_at: string
  channel: string
}

async function fetchToken(
  endpoint: TokenEndpoint,
  body: Record<string, string>,
): Promise<TokenResponse> {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token":
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "",
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`Token request to ${endpoint} failed: ${response.status}`)
  }

  return response.json() as Promise<TokenResponse>
}

async function createClientForChannel(
  endpoint: TokenEndpoint,
  body: Record<string, string>,
): Promise<{ client: Ably.Realtime; channel: string }> {
  const { token, channel } = await fetchToken(endpoint, body)

  const client = new Ably.Realtime({
    authCallback: (_, callback) => {
      fetchToken(endpoint, body)
        .then((tokenResponse) => {
          callback(null, tokenResponse.token)
        })
        .catch((err: unknown) => {
          callback(err as Ably.ErrorInfo, null)
        })
    },
    token,
    autoConnect: true,
  })

  return { client, channel }
}

export type AblySubscription = {
  close: () => void
}

export async function subscribeToOrder<T = unknown>(
  orderId: string,
  handler: (data: T) => void,
): Promise<AblySubscription> {
  const { client, channel } = await createClientForChannel(
    "/ably/order_token",
    { order_id: orderId },
  )

  const ablyChannel = client.channels.get(channel)
  const listener = (message: Ably.Message) => handler(message.data as T)
  await ablyChannel.subscribe(listener)

  return {
    close: () => {
      ablyChannel.unsubscribe(listener)
      client.close()
    },
  }
}

export async function subscribeToCompany<T = unknown>(
  companyId: string,
  handler: (data: T) => void,
): Promise<AblySubscription> {
  const { client, channel } = await createClientForChannel(
    "/ably/company_token",
    { company_id: companyId },
  )

  const ablyChannel = client.channels.get(channel)
  const listener = (message: Ably.Message) => handler(message.data as T)
  await ablyChannel.subscribe(listener)

  return {
    close: () => {
      ablyChannel.unsubscribe(listener)
      client.close()
    },
  }
}
